import axios, { AxiosError, ResponseType } from "axios";
import { AuthPaths } from "../../types/AppTypes";
import { StatusCodes } from "../../constants/AppConstants";

type ContentType = "multipart/form-data" | "application/json";
export const API_ENDPOINT = `${process.env.REACT_APP_MOCKING === "true" ? window.location.origin : process.env.REACT_APP_API_URL}/api/`;

type HttpClientOptions = {
  isAuth?: boolean;
  contentType?: ContentType;
  signal?: AbortSignal;
  responseType?: ResponseType;
  skipUnauthorizedRedirect?: boolean;
};

const HttpClient = {
  async get<TResponse>(
    apiName: string,
    options?: HttpClientOptions,
  ): Promise<TResponse> {
    return this.axiosRequest(apiName, "GET", undefined, options);
  },

  async post<TRequest, TResponse>(
    apiName: string,
    body?: TRequest,
    options?: HttpClientOptions,
  ): Promise<TResponse> {
    return this.axiosRequest<TRequest>(apiName, "POST", body, options);
  },

  async put<TRequest, TResponse>(
    apiName: string,
    body?: TRequest,
    options?: {
      isAuth?: boolean;
      contentType?: ContentType;
      signal?: AbortSignal;
      responseType?: ResponseType;
    },
  ): Promise<TResponse> {
    return this.axiosRequest<TRequest>(apiName, "PUT", body, options);
  },
  async patch<TRequest, TResponse>(
    apiName: string,
    body?: TRequest,
    options?: HttpClientOptions,
  ): Promise<TResponse> {
    return this.axiosRequest<TRequest>(apiName, "PATCH", body, options);
  },
  async delete<TResponse>(
    apiName: string,
    options?: HttpClientOptions,
  ): Promise<TResponse> {
    return this.axiosRequest(apiName, "DELETE", undefined, options);
  },

  async axiosRequest<TPayload>(
    apiName: string,
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
    data?: TPayload,
    options?: HttpClientOptions,
  ) {
    const isAuthenticatedApi =
      options && options.isAuth !== undefined ? options.isAuth : true;
    try {
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage("MOCK_ACTIVATE");

        // Small wait for changes to propagate
        await new Promise((resolve) => {
          setTimeout(resolve, 10);
        });
      }

      const res = await axios({
        baseURL: `${API_ENDPOINT}${apiName}`,
        method,
        withCredentials: true,
        headers: {
          Accept: "application/json",
          "Content-Type": options?.contentType ?? "application/json",
        },
        ...(data && { data }),
        ...(options?.signal && { signal: options.signal }),
        ...(options?.responseType && { responseType: options.responseType }),
      });
      return res.data;
    } catch (error) {
      const errorObj = error as AxiosError;
      console.error("HTTP API ERROR");

      if (errorObj.response && errorObj.request) {
        console.error({
          request_url: `${API_ENDPOINT}${apiName}`,
          request_method: method,
          response_status: errorObj.response.status,
          response_data: errorObj.response.data,
        });

        if (
          isAuthenticatedApi &&
          !options?.skipUnauthorizedRedirect &&
          errorObj.response.status === StatusCodes.UNAUTHORIZED
        ) {
          if (process.env.REACT_APP_ENV === "local") {
            return;
          }
          window.location.href = `${process.env.REACT_APP_API_URL}${AuthPaths.LOGOUT}`;
          return;
        }
      }

      throw error;
    }
  },
};

export default HttpClient;
